/**
 * Semantic colour, label and icon per event type (spec §89).
 *
 * Shared by both timeline views so a colour means the same thing wherever it
 * appears — two views disagreeing on what orange means is worse than one view.
 */
import {
  Activity, Building2, CheckCircle2, GitBranch, IndianRupee, MessageCircle,
  Receipt, Repeat, Target, User,
} from "lucide-react";

import { stageOf } from "@/components/timeline/stages";
import type { EntityType } from "@/types/api";

export const TYPE_META: Record<EntityType, {
  color: string; bg: string; label: string; Icon: typeof Activity;
}> = {
  client:     { color: "#7A5AF8", bg: "#F1EEFE", label: "Client",     Icon: User },
  activity:   { color: "#2563C9", bg: "#E8F0FC", label: "Activity",   Icon: Activity },
  followup:   { color: "#7C3AED", bg: "#F2EBFD", label: "Follow-up",  Icon: Repeat },
  response:   { color: "#0E9AA0", bg: "#E4F5F6", label: "Response",   Icon: MessageCircle },
  commitment: { color: "#4F46E5", bg: "#ECEBFD", label: "Commitment", Icon: CheckCircle2 },
  lead:       { color: "#C2670A", bg: "#FBF0E2", label: "Lead",       Icon: Target },
  admission:  { color: "#2F6B3C", bg: "#E7F1E9", label: "Admission",  Icon: Building2 },
  billing:    { color: "#B4690E", bg: "#FBF1E3", label: "Billing",    Icon: Receipt },
  referral:   { color: "#0E7C86", bg: "#E3F2F4", label: "Commission", Icon: GitBranch },
  conversion: { color: "#0B5D57", bg: "#E3EFED", label: "Approved",   Icon: CheckCircle2 },
  revenue:    { color: "#0B7A5E", bg: "#E4F2ED", label: "Paid",       Icon: IndianRupee },
};

/**
 * Resolved appearance for one event: **colour carries the stage**, icon carries
 * the specific kind.
 *
 * Colouring by entity type made "Response" and "Follow-up" look like unrelated
 * things when both are steps in the same part of the journey. Stage colour ties
 * a node to its rung on the ladder above, so the eye tracks progression down
 * the curve; the icon still distinguishes a call from a gift within a stage.
 */
export function eventMeta(event: { entityType: EntityType; stage?: number }) {
  const stage = stageOf(event.stage);
  const type = TYPE_META[event.entityType];
  return {
    color: stage.color,
    bg: stage.bg,
    /** Stage name — what this node means in the journey. */
    label: stage.name,
    /** Narrower kind, e.g. "Follow-up". Kept for the detail card. */
    typeLabel: type.label,
    Icon: type.Icon,
    stage,
  };
}
